// src/pages/Charts.jsx
// Chart powered by Zerodha Kite API (live) with infinite-scroll history.
// Uses AdvancedChart (lightweight-charts) for rendering.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdvancedChart from '../components/AdvancedChart';
import { authFetch } from '../utils/api';

// ── Constants ─────────────────────────────────────────────────────────────────

const DEFAULT_SYMBOL   = 'RELIANCE';
const DEFAULT_INTERVAL = 'D';

// Per-interval: how many days to fetch on initial load and on each scroll-back
const INTERVAL_CFG = {
  '1':  { label: '1 min',  initDays: 10,  moreDays: 10  },
  '5':  { label: '5 min',  initDays: 30,  moreDays: 30  },
  '15': { label: '15 min', initDays: 60,  moreDays: 60  },
  '60': { label: '1 hr',   initDays: 120, moreDays: 120 },
  'D':  { label: 'Daily',  initDays: 365, moreDays: 365 },
};

const INTERVALS = Object.entries(INTERVAL_CFG).map(([v, c]) => [v, c.label]);

const QUICK = [
  'RELIANCE', 'TCS', 'INFY', 'HDFCBANK',
  'SBIN', 'ICICIBANK', 'HINDUNILVR', 'BAJFINANCE', 'BEL', 'WIPRO',
];

// ── Symbol typeahead ──────────────────────────────────────────────────────────

function SymbolSearch({ value, onChange, onSelect }) {
  const [results,   setResults]   = useState([]);
  const [open,      setOpen]      = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const timer   = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    function click(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', click);
    return () => document.removeEventListener('mousedown', click);
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (!value) { setResults([]); return; }
    timer.current = setTimeout(() => {
      authFetch(`/api/instruments/search?q=${encodeURIComponent(value)}&limit=12`)
        .then(r => r.json())
        .then(d => { setResults(d.symbols || []); setActiveIdx(-1); })
        .catch(() => setResults([]));
    }, 180);
  }, [value]);

  function pick(r) { onSelect(r.symbol); setResults([]); setOpen(false); }

  function onKey(e) {
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && activeIdx >= 0) pick(results[activeIdx]);
    if (e.key === 'Escape') setOpen(false);
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative', flex: '1 1 200px', minWidth: 180, maxWidth: 320 }}>
      <input
        value={value}
        onChange={e => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onKeyDown={onKey}
        placeholder="Search symbol…"
        autoComplete="off"
        spellCheck={false}
        style={inputStyle}
      />
      {open && results.length > 0 && (
        <ul style={dropdownStyle}>
          {results.map((r, i) => (
            <li
              key={r.symbol}
              onMouseDown={() => pick(r)}
              style={{ ...suggestionStyle, background: i === activeIdx ? '#1e293b' : '#0f172a' }}
            >
              <span style={{ fontWeight: 700, fontFamily: 'monospace', minWidth: 80, color: '#e2e8f0' }}>{r.symbol}</span>
              <span style={{ color: '#64748b', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
              {r.type === 'INDEX' && <span style={indexBadge}>IDX</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Indicator toggle button ───────────────────────────────────────────────────

function ToggleBtn({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '0.3rem 0.75rem', fontSize: '0.78rem', fontWeight: 700,
      border: `1px solid ${active ? '#38bdf8' : '#334155'}`,
      background: active ? '#0c4a6e' : '#1e293b',
      color: active ? '#7dd3fc' : '#94a3b8',
      borderRadius: 4, cursor: 'pointer', transition: 'all 0.1s',
    }}>
      {label}
    </button>
  );
}

// ── Merge helper: prepend new bars, skip duplicates ───────────────────────────

function prependBars(older, existing) {
  const seen = new Set(existing.t);
  const idx  = older.t.reduce((acc, t, i) => { if (!seen.has(t)) acc.push(i); return acc; }, []);
  if (!idx.length) return existing;
  const pick = (arr) => idx.map(i => arr[i]);
  return {
    t: [...pick(older.t), ...existing.t],
    o: [...pick(older.o), ...existing.o],
    h: [...pick(older.h), ...existing.h],
    l: [...pick(older.l), ...existing.l],
    c: [...pick(older.c), ...existing.c],
    v: [...pick(older.v), ...existing.v],
  };
}

// ── Page component ────────────────────────────────────────────────────────────

export default function Charts() {
  const [inputVal, setInputVal] = useState(DEFAULT_SYMBOL);
  const [symbol,   setSymbol]   = useState(DEFAULT_SYMBOL);
  const [interval, setInterval] = useState(DEFAULT_INTERVAL);
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,    setError]    = useState(null);
  const [noMore,   setNoMore]   = useState(false);   // true when Kite returned 0 bars
  const [usingFallback, setUsingFallback] = useState(false); // true when showing DB data instead of Kite

  // Indicator toggles
  const [showVolume, setShowVolume] = useState(true);
  const [showEMA,    setShowEMA]    = useState(false);
  const [showRSI,    setShowRSI]    = useState(false);
  const [showMACD,   setShowMACD]   = useState(false);

  // Keep latest data in a ref so the onLoadMore callback is never stale
  const dataRef = useRef(null);
  dataRef.current = data;

  // ── Kite API fetch ──────────────────────────────────────────────────────────
  function buildUrl(sym, intv, days, toTs = null) {
    let url = `/api/kite/history?symbol=${encodeURIComponent(sym)}&interval=${intv}&days=${days}`;
    if (toTs) url += `&to_ts=${toTs}`;
    return url;
  }

  // Initial load — replaces all existing data
  useEffect(() => {
    setData(null);
    setError(null);
    setNoMore(false);
    setUsingFallback(false);
    setLoading(true);

    const { initDays } = INTERVAL_CFG[interval] || INTERVAL_CFG['D'];

    authFetch(buildUrl(symbol, interval, initDays))
      .then(r => {
        if (!r.ok) return r.json().then(e => Promise.reject({ detail: e.detail || `Error ${r.status}`, status: r.status }));
        return r.json();
      })
      .then(d => {
        if (!d.t?.length) return Promise.reject({ detail: `No data returned for ${symbol}. Check that Kite token is valid.`, status: 200 });
        setData(d);
        setLoading(false);
      })
      .catch(async (e) => {
        const detail = typeof e === 'string' ? e : (e.detail || e.message || 'Unknown error');
        const status = e.status || 0;
        // Detect Kite errors (502 = bad gateway from Kite, 400 = token issue, or error mentions Kite/token)
        const isKiteErr = status === 502 || status === 400 ||
          detail.toLowerCase().includes('kite') ||
          detail.toLowerCase().includes('token') ||
          detail.toLowerCase().includes('not configured');
        // Only Daily and 5min have DB data; 1min/15min/60min are Kite-only
        const canFallback = ['D', '5'].includes(interval);

        if (isKiteErr && canFallback) {
          const resolution = interval === 'D' ? 'D' : '5min';
          try {
            const dbRes = await authFetch(
              `/api/history/db?symbol=${encodeURIComponent(symbol)}&resolution=${resolution}&days=365`
            );
            if (!dbRes.ok) throw new Error('DB query failed');
            const dbData = await dbRes.json();
            if (!dbData.t?.length) throw new Error(`No local data for ${symbol}`);
            setUsingFallback(true);
            setData(dbData);
            setLoading(false);
            return;
          } catch {
            // DB fallback also failed — show original Kite error
          }
        }
        setError(detail);
        setLoading(false);
      });
  }, [symbol, interval]); // eslint-disable-line

  // ── Infinite scroll callback (called by AdvancedChart when user scrolls left) ─
  const handleLoadMore = useCallback((oldestTs) => {
    if (usingFallback || loadingMore || noMore) return; // DB fallback has no pagination
    const { moreDays } = INTERVAL_CFG[interval] || INTERVAL_CFG['D'];
    setLoadingMore(true);

    authFetch(buildUrl(symbol, interval, moreDays, oldestTs))
      .then(r => r.json())
      .then(d => {
        if (!d.t?.length) { setNoMore(true); return; }
        setData(prev => prev ? prependBars(d, prev) : d);
      })
      .catch(() => {/* silently ignore load-more errors */})
      .finally(() => setLoadingMore(false));
  }, [symbol, interval, loadingMore, noMore, usingFallback]);

  function handleSelect(sym) { setInputVal(sym); setSymbol(sym); }

  const chartHeight = Math.max(500, window.innerHeight - 280);

  return (
    <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem', background: '#0f172a', minHeight: '100vh' }}>

      {/* Header */}
      <h1 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 800, color: '#e2e8f0' }}>
        Advanced Chart
        <span style={{ marginLeft: '0.6rem', fontSize: '0.75rem', fontWeight: 500, color: '#475569', verticalAlign: 'middle' }}>
          {usingFallback ? 'Local DB data' : 'Live from Zerodha Kite · scroll left for more history'}
        </span>
      </h1>

      {/* Kite fallback banner */}
      {usingFallback && (
        <div style={{
          padding: '0.45rem 0.85rem', borderRadius: 6,
          background: '#1c1a05', border: '1px solid #78350f',
          color: '#fbbf24', fontSize: '0.78rem',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <span>⚠️</span>
          <span>
            Showing local DB data — Kite not configured. Daily &amp; 5min only, no scroll-back.{' '}
            <a href="/settings" style={{ color: '#fbbf24', fontWeight: 700 }}>Set up Kite →</a>
          </span>
        </div>
      )}

      {/* Controls */}
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.6rem',
        alignItems: 'flex-start',
        alignContent: 'flex-start',
        padding: '0.65rem 1rem',
        background: '#1e293b',
        border: '1px solid #334155',
        borderRadius: 8,
      }}>
        {/* Symbol typeahead */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', flex: '1 1 200px', minWidth: 180, maxWidth: 320 }}>
          <span style={labelStyle}>Symbol</span>
          <SymbolSearch value={inputVal} onChange={setInputVal} onSelect={handleSelect} />
        </div>

        {/* Interval */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <span style={labelStyle}>Interval</span>
          <select value={interval} onChange={e => setInterval(e.target.value)} style={selectStyle}>
            {INTERVALS.map(([v, l]) => (
              <option key={v} value={v} disabled={usingFallback && !['D', '5'].includes(v)}>
                {l}{usingFallback && !['D', '5'].includes(v) ? ' (Kite only)' : ''}
              </option>
            ))}
          </select>
        </div>

        {/* Indicators */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
          <span style={labelStyle}>Indicators</span>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <ToggleBtn label="Vol"  active={showVolume} onClick={() => setShowVolume(v => !v)} />
            <ToggleBtn label="EMA"  active={showEMA}    onClick={() => setShowEMA(v => !v)} />
            <ToggleBtn label="RSI"  active={showRSI}    onClick={() => setShowRSI(v => !v)} />
            <ToggleBtn label="MACD" active={showMACD}   onClick={() => setShowMACD(v => !v)} />
          </div>
        </div>

        {/* Loading-more indicator */}
        {loadingMore && (
          <div style={{ alignSelf: 'center', color: '#38bdf8', fontSize: '0.8rem', fontWeight: 600 }}>
            ← loading history…
          </div>
        )}
        {noMore && (
          <div style={{ alignSelf: 'center', color: '#475569', fontSize: '0.78rem' }}>
            ← no more history
          </div>
        )}
      </div>

      {/* Quick symbols */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
        {QUICK.map(s => (
          <button key={s} onClick={() => handleSelect(s)} style={{
            padding: '0.22rem 0.65rem', fontSize: '0.75rem', fontWeight: 700,
            border: `1px solid ${symbol === s ? '#38bdf8' : '#334155'}`,
            background: symbol === s ? '#0c4a6e' : '#1e293b',
            color: symbol === s ? '#7dd3fc' : '#94a3b8',
            borderRadius: 4, cursor: 'pointer', fontFamily: 'monospace',
            transition: 'all 0.1s',
          }}>
            {s}
          </button>
        ))}
      </div>

      {/* Loading skeleton */}
      {loading && (
        <div style={{
          height: chartHeight, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: '#131722', borderRadius: 4, border: '1px solid #2a2e39', color: '#475569',
          fontSize: '0.9rem',
        }}>
          Loading {symbol}…
        </div>
      )}

      {/* Error */}
      {error && !loading && (
        <div style={{
          padding: '0.75rem 1rem', background: '#1e1215', border: '1px solid #7f1d1d',
          borderRadius: 6, color: '#fca5a5', fontSize: '0.85rem',
        }}>
          {error}
        </div>
      )}

      {/* Chart */}
      {!loading && !error && (
        <AdvancedChart
          data={data}
          height={chartHeight}
          showVolume={showVolume}
          showEMA={showEMA}
          showRSI={showRSI}
          showMACD={showMACD}
          onLoadMore={handleLoadMore}
        />
      )}

      <p style={{ margin: 0, fontSize: '0.7rem', color: '#334155', textAlign: 'center' }}>
        Data via Zerodha Kite API · scroll left to load more history · indicators computed locally
      </p>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const labelStyle = {
  fontSize: '0.7rem', fontWeight: 700, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.05em',
};
const inputStyle = {
  width: '100%', padding: '0.48rem 0.65rem', fontSize: '0.9rem',
  fontWeight: 600, fontFamily: 'monospace', letterSpacing: '0.03em',
  border: '1px solid #334155', borderRadius: 6, outline: 'none',
  background: '#0f172a', color: '#e2e8f0',
};
const selectStyle = {
  padding: '0.48rem 0.5rem', fontSize: '0.88rem',
  border: '1px solid #334155', borderRadius: 6,
  background: '#0f172a', color: '#e2e8f0',
};
const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
  background: '#0f172a', border: '1px solid #334155', borderRadius: 6,
  boxShadow: '0 6px 20px rgba(0,0,0,0.4)',
  listStyle: 'none', margin: '2px 0 0', padding: 0,
  maxHeight: 320, overflowY: 'auto',
};
const suggestionStyle = {
  display: 'flex', alignItems: 'center', gap: '0.5rem',
  padding: '0.45rem 0.75rem', cursor: 'pointer', fontSize: '0.87rem',
};
const indexBadge = {
  marginLeft: 'auto', flexShrink: 0,
  background: '#1e3a5f', color: '#7dd3fc',
  borderRadius: 3, padding: '1px 5px',
  fontSize: '0.65rem', fontWeight: 700,
};
